import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";

const API_URL = `${getAPIbaseURL()}/presences`;

export const startPause = createAsyncThunk(
  "presences/startPause",
  async ({ presenceId, reason, start_location, start_accuracy, start }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const body = {
        reason,
        start_location,
        start_accuracy,
        start,
      };
      const response = await axios.post(
        `${API_URL}/${presenceId}/pauses/start`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.startPauseError"));
    }
  }
);

export const endPause = createAsyncThunk(
  "presences/endPause",
  async (pauseId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/pauses/${pauseId}/end`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.endPauseError"));
    }
  }
);

export const createPresence = createAsyncThunk(
  "presences/createPresence",
  async (presenceData, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/`, presenceData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.createPresenceError"));
    }
  }
);

export const checkLocation = createAsyncThunk(
  "presences/checkLocation",
  async ({ location, accuracy, mode }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/location-check`,
        { location, accuracy, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || null);
    }
  }
);

export const fetchPresences = createAsyncThunk(
  "presences/fetchPresences",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.fetchPresencesError"));
    }
  }
);

export const getCurrentPresence = createAsyncThunk(
  "presences/getCurrentPresence",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      return data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.getCurrentPresenceError"));
    }
  }
);

export const getCurrentPresenceByUser = createAsyncThunk(
  "presences/getCurrentPresenceByUser",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/current/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      return thunkAPI.rejectWithValue(
        error.response?.data || i18n.t("slices.presencesSlice.getCurrentPresenceError")
      );
    }
  }
);

export const fetchUserPresences = createAsyncThunk(
  "presences/fetchUserPresences",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.fetchUserPresencesError"));
    }
  }
);

export const editPresenceDetails = createAsyncThunk(
  "presences/editPresenceDetails",
  async ({ presenceId, updates }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/${presenceId}/edit`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.editPresenceDetailsError"));
    }
  }
);

export const fetchPresenceById = createAsyncThunk(
  "presences/fetchPresenceById",
  async (presenceId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/${presenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.fetchPresenceByIdError"));
    }
  }
);

export const fetchActivePresences = createAsyncThunk(
  "presences/fetchActivePresences",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.fetchActivePresencesError"));
    }
  }
);

export const filterPresences = createAsyncThunk(
  "presences/filterPresences",
  async (filterParams, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const query = new URLSearchParams(filterParams).toString();
      const response = await axios.get(`${API_URL}/filter?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.filterPresencesError"));
    }
  }
);

export const updatePresence = createAsyncThunk(
  "presences/updatePresence",
  async ({ presenceId, ...updateData }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/${presenceId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.updatePresenceError"));
    }
  }
);

export const deletePresence = createAsyncThunk(
  "presences/deletePresence",
  async (presenceId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${presenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return presenceId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.presencesSlice.deletePresenceError"));
    }
  }
);

const presencesSlice = createSlice({
  name: "presences",
  initialState: {
    items: [],
    currentPresence: null,
    activePause: null,
    isLoading: false,
    error: null,
    message: null,
  },
  reducers: {
    setActivePause: (state, action) => {
      state.activePause = action.payload;
    },
    clearActivePause: (state) => {
      state.activePause = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startPause.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startPause.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activePause = action.payload;
        state.message = i18n.t("slices.presencesSlice.pauseStartedSuccess");
      })
      .addCase(startPause.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(endPause.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(endPause.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activePause = null;
        state.message = i18n.t("slices.presencesSlice.pauseEndedSuccess");

        if (state.currentPresence) {
          state.currentPresence.pauses = state.currentPresence.pauses || [];
          const pauseIndex = state.currentPresence.pauses.findIndex(
            p => p.id === action.payload.id
          );
          if (pauseIndex !== -1) {
            state.currentPresence.pauses[pauseIndex] = action.payload;
          } else {
            state.currentPresence.pauses.push(action.payload);
          }
        }
      })
      .addCase(endPause.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createPresence.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPresence.fulfilled, (state, action) => {
        state.isLoading = false;
        const idx = state.items.findIndex(p => p.id === action.payload.id);
        if (idx === -1) {
          state.items.push(action.payload);
        } else {
          state.items[idx] = action.payload;
        }
        state.message = i18n.t("slices.presencesSlice.presenceCreatedSuccess");
      })
      .addCase(createPresence.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPresences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPresences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchPresences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getCurrentPresence.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrentPresence.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPresence = action.payload;
      })
      .addCase(getCurrentPresence.rejected, (state, action) => {
        state.isLoading = false;
        state.currentPresence = null;
        state.error = action.payload;
      })
      .addCase(getCurrentPresenceByUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrentPresenceByUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPresence = action.payload;
      })
      .addCase(getCurrentPresenceByUser.rejected, (state, action) => {
        state.isLoading = false;
        state.currentPresence = null;
        state.error = action.payload;
      })
      .addCase(fetchUserPresences.fulfilled, (state, action) => {
        state.items = action.payload;
      })
      .addCase(updatePresence.fulfilled, (state, action) => {
        const index = state.items.findIndex(
          (presence) => presence.id === action.payload.id
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.message = i18n.t("slices.presencesSlice.presenceUpdatedSuccess");
      })
      .addCase(editPresenceDetails.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex((p) => p.id === updated.id);
        if (index !== -1) {
          state.items[index] = updated;
        }
        state.message = i18n.t("slices.presencesSlice.presenceDetailsUpdatedSuccess");
      })
      .addCase(fetchPresenceById.fulfilled, (state, action) => {
        const fetched = action.payload;
        const exists = state.items.find((p) => p.id === fetched.id);
        if (!exists) state.items.push(fetched);
      })
      .addCase(fetchActivePresences.fulfilled, (state, action) => {
        state.items = action.payload;
      })
      .addCase(filterPresences.fulfilled, (state, action) => {
        state.items = action.payload;
      })
      .addCase(deletePresence.fulfilled, (state, action) => {
        state.items = state.items.filter(
          (presence) => presence.id !== action.payload
        );
        state.message = i18n.t("slices.presencesSlice.presenceDeletedSuccess");
      });
  },
});

export const { setActivePause, clearActivePause } = presencesSlice.actions;
export default presencesSlice.reducer;